import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Clock, Calendar } from "lucide-react";
import { fetchBlog, fetchBlogPost } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { ProductImage } from "../components/ProductImage";
import { useDocumentMeta } from "../hooks/use-document-meta";

export function BlogList() {
  useDocumentMeta({
    title: "Journal — Insights grossiste fast-food & fournisseur restauration rapide",
    description:
      "Le terrain, les chiffres, les tendances pour la restauration rapide. Articles courts et actionnables pour gérants fast-food, kebab, burger, dark kitchen et food truck — par votre grossiste fournisseur Frites Dorées.",
    canonicalPath: "/blog",
  });
  const [posts, setPosts] = useState([]);
  useEffect(() => { window.scrollTo(0, 0); fetchBlog().then(setPosts); }, []);

  return (
    <main data-testid="blog-page" className="bg-[#050505]">
      <PageHeader
        kicker="Blog — Insights B2B"
        title={<>Le terrain. <br /><span className="text-[#FFC300]">Les chiffres.</span><br />Les tendances.</>}
        subtitle="Articles courts et actionnables pour faire tourner votre cuisine plus vite, plus propre, plus rentable."
      />
      <section className="px-6 md:px-10 lg:px-14 py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {posts.map((p, i) => (
            <motion.article
              key={p.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              data-testid={`blog-card-${p.slug}`}
            >
              <Link to={`/blog/${p.slug}`} className="block group">
                <div className="product-frame aspect-[4/3] relative overflow-hidden">
                  <ProductImage path={p.cover} alt={p.title} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-contain p-6 md:p-8" />
                  <span className="absolute top-4 left-4 tag-label bg-black text-white px-2 py-1">{p.category}</span>
                </div>
                <div className="mt-5">
                  <div className="flex items-center gap-4 text-[11px] tracking-widest uppercase text-zinc-500 font-medium">
                    <span className="flex items-center gap-1.5"><Calendar size={12} /> {formatDate(p.date)}</span>
                    <span className="flex items-center gap-1.5"><Clock size={12} /> {p.reading_time}</span>
                  </div>
                  <h3 className="font-display text-xl md:text-2xl uppercase tracking-tight mt-3 leading-tight group-hover:text-[#FFC300] transition-colors">{p.title}</h3>
                  <p className="font-body text-sm text-zinc-400 mt-3 leading-relaxed">{p.excerpt}</p>
                  <span className="inline-flex items-center gap-1.5 tag-label text-[#FFC300] mt-5"><span className="hover-underline">Lire l'article</span> <ArrowUpRight size={12} /></span>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>
      </section>
    </main>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useDocumentMeta({
    title: post?.title || (loading ? null : "Article introuvable"),
    description: post?.excerpt || null,
    canonicalPath: post ? `/blog/${post.slug}` : null,
    robots: !loading && !post ? "noindex" : "index",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    fetchBlogPost(slug).then(setPost).catch(() => setPost(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <main className="min-h-screen pt-40 text-center text-zinc-400" data-testid="blog-loading">Chargement...</main>;
  if (!post) return (
    <main className="min-h-screen pt-40 px-6 text-center" data-testid="blog-not-found">
      <h1 className="font-display text-4xl mb-4">Article introuvable</h1>
      <Link to="/blog" className="btn-primary inline-flex">Tous les articles</Link>
    </main>
  );

  // Parse body: split on double newlines, render paragraphs; handle **bold**
  const blocks = post.body.split(/\n\n+/).filter(Boolean);

  return (
    <main data-testid="blog-post-page" className="bg-[#050505]">
      <article className="pt-36 md:pt-44 px-6 md:px-10 lg:px-14">
        <Link to="/blog" className="tag-label inline-flex items-center gap-2 hover:text-[#FFC300] transition-colors mb-10" data-testid="back-to-blog">
          <ArrowLeft size={14} /> Retour au blog
        </Link>

        <div className="max-w-4xl">
          <div className="tag-label text-[#FFC300] mb-4">{post.category}</div>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.95]" data-testid="blog-post-title">{post.title}</h1>
          <div className="mt-8 flex items-center gap-5 text-xs tracking-widest uppercase text-zinc-500">
            <span className="flex items-center gap-2"><Calendar size={12} /> {formatDate(post.date)}</span>
            <span className="flex items-center gap-2"><Clock size={12} /> {post.reading_time}</span>
          </div>
        </div>

        <div className="mt-12 product-frame aspect-[21/9] max-w-6xl relative overflow-hidden">
          <ProductImage path={post.cover} alt={post.title} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-contain p-10" />
        </div>

        <div className="max-w-3xl mt-14 mb-24 space-y-6" data-testid="blog-post-body">
          {blocks.map((b, i) => (
            <p key={i} className="font-body text-lg md:text-xl leading-relaxed text-zinc-200">
              {renderInlineMarkdown(b)}
            </p>
          ))}
        </div>
      </article>

      <section className="border-t border-white/10 py-16 lg:py-20 px-6 md:px-10 lg:px-14 bg-[#FFC300] text-black">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tighter leading-none">Un besoin produit ? <br /> Parlons-en.</h2>
          <Link to="/contact" className="btn-secondary !border-black !text-black hover:!bg-black hover:!text-[#FFC300]">Parler à un commercial <ArrowUpRight size={18} /></Link>
        </div>
      </section>
    </main>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}


function renderInlineMarkdown(text) {
  const lines = String(text ?? "").split("\n");
  const out = [];

  lines.forEach((line, lineIndex) => {
    const parts = line.split(/(\*\*.+?\*\*)/g).filter(Boolean);
    parts.forEach((part, partIndex) => {
      if (part.startsWith("**") && part.endsWith("**") && part.length >= 4) {
        out.push(
          <strong key={`strong-${lineIndex}-${partIndex}`} className="text-[#FFC300] font-semibold">
            {part.slice(2, -2)}
          </strong>
        );
      } else {
        out.push(part);
      }
    });
    if (lineIndex < lines.length - 1) {
      out.push(<br key={`br-${lineIndex}`} />);
    }
  });

  return out;
}
